import { useState, useMemo } from "react";
import { getToday, addDays } from "../../shared/types/date-helpers";

interface DatePickerProps {
  readonly value: string | null;
  readonly onChange: (date: string | null) => void;
  readonly onClose: () => void;
}

const WEEK_DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"] as const;

export function DatePicker({ value, onChange, onClose }: DatePickerProps) {
  const today = getToday();
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = value ? new Date(value + "T00:00:00") : new Date();
    return { year: date.getFullYear(), month: date.getMonth() };
  });

  // Générer les jours du mois
  const monthDays = useMemo(() => {
    const { year, month } = currentMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Jour de la semaine du premier jour (0 = Dimanche, 1 = Lundi...)
    // On veut 0 = Lundi, 6 = Dimanche
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;

    const days: Array<{ date: string; day: number; isCurrentMonth: boolean }> = [];

    // Jours du mois précédent
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date: d.toISOString().split("T")[0],
        day: d.getDate(),
        isCurrentMonth: false,
      });
    }

    // Jours du mois actuel
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(year, month, i);
      days.push({
        date: d.toISOString().split("T")[0],
        day: i,
        isCurrentMonth: true,
      });
    }

    // Jours du mois suivant
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const d = new Date(year, month + 1, i);
      days.push({
        date: d.toISOString().split("T")[0],
        day: i,
        isCurrentMonth: false,
      });
    }

    return days;
  }, [currentMonth]);

  const monthNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  const goToPrevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { ...prev, month: prev.month - 1 };
    });
  };

  const goToNextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { ...prev, month: prev.month + 1 };
    });
  };

  const isSelected = (date: string) => value === date;
  const isToday = (date: string) => date === today;

  return (
    <div className="bg-surface border border-border rounded-2xl shadow-xl p-4 w-full max-w-xs md:max-w-xs">
      {/* Header avec navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={goToPrevMonth}
          className="p-1 rounded-lg hover:bg-surface-alt text-ink-faint hover:text-ink transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}>
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-ink">
          {monthNames[currentMonth.month]} {currentMonth.year}
        </span>
        <button
          type="button"
          onClick={goToNextMonth}
          className="p-1 rounded-lg hover:bg-surface-alt text-ink-faint hover:text-ink transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Jours de la semaine */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEK_DAYS.map((day) => (
          <div key={day} className="text-center text-[10px] text-ink-faint font-medium py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Jours du mois */}
      <div className="grid grid-cols-7 gap-1">
        {monthDays.map(({ date, day, isCurrentMonth }) => (
          <button
            key={date}
            type="button"
            onClick={() => {
              onChange(date);
              onClose();
            }}
            className={`
              relative h-8 w-8 text-xs font-medium rounded-full transition-all
              ${!isCurrentMonth ? "text-ink-faint/50" : ""}
              ${isSelected(date)
                ? "bg-accent text-white shadow-md"
                : isToday(date)
                  ? "bg-accent/10 text-accent hover:bg-accent/20"
                  : "text-ink hover:bg-surface-alt"
              }
            `}
          >
            {day}
            {isToday(date) && !isSelected(date) && (
              <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-accent rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Footer avec raccourcis */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/60">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => {
              onChange(today);
              onClose();
            }}
            className="px-2 py-1 text-[10px] font-medium rounded-full bg-surface text-ink-muted hover:text-ink border border-border/60 transition-colors"
          >
            Auj.
          </button>
          <button
            type="button"
            onClick={() => {
              onChange(addDays(today, 1));
              onClose();
            }}
            className="px-2 py-1 text-[10px] font-medium rounded-full bg-surface text-ink-muted hover:text-ink border border-border/60 transition-colors"
          >
            Dem.
          </button>
          <button
            type="button"
            onClick={() => {
              onChange(addDays(today, 7));
              onClose();
            }}
            className="px-2 py-1 text-[10px] font-medium rounded-full bg-surface text-ink-muted hover:text-ink border border-border/60 transition-colors"
          >
            +1 sem
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            onClose();
          }}
          className="px-2 py-1 text-[10px] font-medium rounded-full text-danger hover:bg-danger/10 transition-colors"
        >
          Effacer
        </button>
      </div>
    </div>
  );
}
